import { LeaseRow } from '../model/lease-row';
import { Article } from '../model/article';
import { LocalDate } from '../../core/model/local-date';
import { Currency, Money } from '../../core/model/money';
import { Phrase } from '../../core/model/phrase';
import { LanguageService } from '../../core/service/language.service';
import { TaxTypeCode } from '../model/tax-type-code';
import { PropertyTaxRentalChangeModel } from '../model/property-tax-rental-change';
import { getPeriodAmount } from './rent-calculator';

export function toPropertyTaxPercentageDisplayValue(value: number | null | undefined): number | null {
  if (value == null) return null;
  const percentageValue = value * 100;
  if (!isFinite(percentageValue)) return null;
  return Number(Math.round(Number(`${percentageValue}e2`)) + 'e-2');
}

export function applyPropertyTaxAmounts(row: LeaseRow, change: PropertyTaxRentalChangeModel, currencyCode: string): void {
  const currency = currencyCode ?? row?.currencyCode ?? row?.article?.currencyCode ?? Currency.SEK;
  row.yearRent = new Money(currency, change?.taxPerYear?.amount ?? 0);
  row.currencyCode = currency;
  row.amount =
    row?.article?.timeUnit != null && row?.chargedMonthsLength > 0
      ? getPeriodAmount(row.yearRent, row.article.timeUnit, row.chargedMonthsLength).round(2)
      : new Money(currency, change?.taxPerMonth?.amount ?? 0);
}

function ensureInitializedMoney(row: LeaseRow, currencyCode?: string): void {
  const currency = currencyCode ?? row?.currencyCode ?? row?.article?.currencyCode ?? Currency.SEK;
  row.currencyCode = currency;
  if (!row.amount || row.amount.amount == null || !row.amount.currencyCode) {
    row.amount = new Money(currency, row.amount?.amount ?? 0);
  }
  if (!row.yearRent || row.yearRent.amount == null || !row.yearRent.currencyCode) {
    row.yearRent = new Money(currency, row.yearRent?.amount ?? 0);
  }
}

export function hydrateLeaseRowCalculatedAmounts(row: LeaseRow, fallbackCurrency?: string, forceHydrateCalculatedAmounts = false): void {
  if (!row) return;
  const vatPercent = row.article?.vatPercent ?? 0;
  const currency = row.currencyCode ?? fallbackCurrency;
  if (!currency) return;

  const isUiRow = !row.id || (typeof row.id === 'string' && row.id.trim().length === 0);
  const shouldHydrate = (moneyField?: Money | null): boolean => {
    if (forceHydrateCalculatedAmounts) return true;
    if (isUiRow) return true;
    if (!moneyField) return true;
    return (moneyField as any).amount == null;
  };

  if (row.amount?.amount != null) {
    const net = row.amount.amount;
    if (shouldHydrate(row.periodRent)) {
      row.periodRent = new Money(currency, net).round(2);
    }
    const gross = net + (net * vatPercent) / 100;
    if (shouldHydrate(row.periodRentInclVat)) {
      row.periodRentInclVat = new Money(currency, gross).round(2);
    }
    if (forceHydrateCalculatedAmounts || isUiRow || row.rentThisPeriod == null) {
      row.rentThisPeriod = net;
    }
    if (forceHydrateCalculatedAmounts || isUiRow || row.rentThisPeriodInclVat == null) {
      row.rentThisPeriodInclVat = row.periodRentInclVat?.amount ?? gross;
    }
  }

  if (row.yearRent?.amount != null) {
    const netYear = row.yearRent.amount;
    const grossYear = netYear + (netYear * vatPercent) / 100;
    if (shouldHydrate(row.yearRentInclVat)) {
      row.yearRentInclVat = new Money(currency, grossYear).round(2);
    }
  }
}

export function ensureInvoiceText(row: LeaseRow, article: Article, languageService: LanguageService, cultureName?: string): void {
  if (!row) return;
  const translation = new Phrase(article?.invoiceTexts ?? [], languageService).getTranslationWithFallback(cultureName);
  row.invoiceText = translation;
}

export function ensurePropertyTaxAmounts(
  row: LeaseRow,
  change: PropertyTaxRentalChangeModel | undefined,
  currencyCode: string | undefined,
  forceHydrateCalculatedAmounts = false,
  overwritePropertyTaxAmounts = false
): void {
  if (change && (overwritePropertyTaxAmounts || !row.amount || row.amount.amount == null || !row.yearRent || row.yearRent.amount == null)) {
    applyPropertyTaxAmounts(row, change, currencyCode);
  }
  ensureInitializedMoney(row, currencyCode);
  hydrateLeaseRowCalculatedAmounts(row, currencyCode, forceHydrateCalculatedAmounts);
}

export function getLatestActivePropertyTaxRow(rows: LeaseRow[], options?: { onlyPersisted?: boolean }): LeaseRow | undefined {
  const today = LocalDate.today();
  return (rows ?? [])
    .filter(row => row?.article?.isPropertyTax)
    .filter(row => {
      const from = row?.fromDate;
      const to = row?.toDate;
      if (from && from.hasValue() && LocalDate.GreaterThan(from, today)) return false;
      if (!to || !to.hasValue()) return true;
      return LocalDate.GreaterThan(to, today);
    })
    .filter(row => (options?.onlyPersisted ? !!row?.id && row.id.trim().length > 0 : true))
    .sort((a, b) => {
      const bValue = Number.isFinite(b?.fromDate?.valueOf()) ? b.fromDate.valueOf() : -Infinity;
      const aValue = Number.isFinite(a?.fromDate?.valueOf()) ? a.fromDate.valueOf() : -Infinity;
      return bValue - aValue;
    })[0];
}

export function getLatestPropertyTaxRow(rows: LeaseRow[]): LeaseRow | undefined {
  return (rows ?? [])
    .filter(row => row?.article?.isPropertyTax)
    .sort((a, b) => {
      const bValue = Number.isFinite(b?.fromDate?.valueOf()) ? b.fromDate.valueOf() : -Infinity;
      const aValue = Number.isFinite(a?.fromDate?.valueOf()) ? a.fromDate.valueOf() : -Infinity;
      return bValue - aValue;
    })[0];
}

export type PropertyTaxRowSignature = {
  articleId: string | null;
  calculationBasis: unknown | null;
  calculationMethod: unknown | null;
  percentage?: unknown | null;
};

export type PropertyTaxRowMutationMode = 'editInPlace' | 'replaceFromToday' | 'closeOnly';

export function buildPropertyTaxRowSignature(
  articleId: string | null,
  calculationBasis: unknown | null,
  calculationMethod: unknown | null,
  options?: {
    percentage?: unknown | null;
  }
): PropertyTaxRowSignature {
  return {
    articleId,
    calculationBasis,
    calculationMethod,
    percentage: options?.percentage ?? null,
  };
}

export type PropertyTaxRowMutationResult = {
  targetRow?: LeaseRow;
  uiReplacementRow?: LeaseRow;
  baselineRowClosedByUi: boolean;
};

export function propertyTaxRowSignaturesMatch(
  currentSignature?: PropertyTaxRowSignature | null,
  baselineSignature?: PropertyTaxRowSignature | null
): boolean {
  if (!currentSignature || !baselineSignature) return false;

  const normalize = (value: unknown): string | null => {
    if (value == null) return null;
    return `${value}`;
  };

  return (
    normalize(currentSignature.articleId) === normalize(baselineSignature.articleId) &&
    normalize(currentSignature.calculationBasis) === normalize(baselineSignature.calculationBasis) &&
    normalize(currentSignature.calculationMethod) === normalize(baselineSignature.calculationMethod) &&
    normalize(currentSignature.percentage) === normalize(baselineSignature.percentage)
  );
}

export function getCurrentUiPropertyTaxReplacementRow(
  rows: LeaseRow[] | undefined,
  options: {
    uiReplacementRowRef?: LeaseRow;
    nonRetroRowRef?: LeaseRow;
  }
): LeaseRow | undefined {
  const activeRows = rows ?? [];
  if (options.uiReplacementRowRef && activeRows.includes(options.uiReplacementRowRef)) {
    return options.uiReplacementRowRef;
  }

  if (options.nonRetroRowRef && activeRows.includes(options.nonRetroRowRef) && !isPersistedLeaseRow(options.nonRetroRowRef)) {
    return options.nonRetroRowRef;
  }

  return undefined;
}

export function applyResolvedPropertyTaxMutationResult(
  result: PropertyTaxRowMutationResult,
  options: {
    setBaselineRowClosedByUi: (value: boolean) => void;
    setUiReplacementRow: (row?: LeaseRow) => void;
    setUiReplacementRowCreatedByUi: (value: boolean) => void;
    setNonRetroRow: (row: LeaseRow, createdByUi: boolean) => void;
    resetPropertyTaxUiState: () => void;
  }
): void {
  options.setBaselineRowClosedByUi(result.baselineRowClosedByUi);
  options.setUiReplacementRow(result.uiReplacementRow);
  options.setUiReplacementRowCreatedByUi(!!result.uiReplacementRow);

  if (result.targetRow) {
    options.setNonRetroRow(result.targetRow, result.targetRow === result.uiReplacementRow);
    return;
  }

  options.resetPropertyTaxUiState();
}

export function shouldHandlePropertyTaxUserChange(suspendPropertyTaxUserTriggers: boolean): boolean {
  return !suspendPropertyTaxUserTriggers;
}

export function runWithPropertyTaxUserTriggersSuspended(
  setSuspendPropertyTaxUserTriggers: (value: boolean) => void,
  action: () => void
): void {
  setSuspendPropertyTaxUserTriggers(true);
  try {
    action();
  } finally {
    setTimeout(() => {
      setSuspendPropertyTaxUserTriggers(false);
    }, 0);
  }
}

function removeLeaseRow(rows: LeaseRow[], row?: LeaseRow): void {
  if (!row) return;
  const index = rows.indexOf(row);
  if (index > -1) {
    rows.splice(index, 1);
  }
}

function restoreBaselinePropertyTaxRow(row: LeaseRow, originalToDate?: LocalDate | null): void {
  const restoreValue = originalToDate && originalToDate.hasValue() ? LocalDate.fromDate(originalToDate) : undefined;
  if (restoreValue) {
    row.toDate = restoreValue;
  } else {
    (row as any)._toDate = undefined;
  }
}

function closePropertyTaxRow(rows: LeaseRow[], row: LeaseRow | undefined, closeDate: LocalDate): boolean {
  if (!row) return false;
  if (!isPersistedLeaseRow(row)) {
    removeLeaseRow(rows, row);
    return true;
  }

  row.toDate = LocalDate.fromDate(closeDate);
  return true;
}

export function getOrCreateUiPropertyTaxRow(
  existingRow: LeaseRow | undefined,
  startDate: LocalDate,
  selectedArticle: Article,
  propertyTaxChange: PropertyTaxRentalChangeModel | undefined,
  context: { currencyCode?: string; rentalObjectSize?: number; invoicePeriod?: number },
  languageService: LanguageService,
  options?: { forceStartDate?: boolean }
): { row: LeaseRow; createdByUi: boolean } {
  const row = existingRow ?? new LeaseRow(context.currencyCode ?? selectedArticle?.currencyCode ?? Currency.SEK);
  const createdByUi = !existingRow;

  row.article = selectedArticle;
  ensureInvoiceText(row, row.article, languageService, languageService.currentCultureLang);
  row.currencyCode = context.currencyCode ?? row.currencyCode ?? selectedArticle?.currencyCode ?? Currency.SEK;
  row.rentalObjectSize = context.rentalObjectSize;
  row.leaseAgreementInvoicePeriod = context.invoicePeriod;
  if (options?.forceStartDate || !row.fromDate || !row.fromDate.hasValue()) {
    row.fromDate = LocalDate.fromDate(startDate);
  }
  ensurePropertyTaxAmounts(row, propertyTaxChange, context.currencyCode, !!existingRow);

  return { row, createdByUi };
}

export function resolvePropertyTaxRowMutation(
  leaseRows: LeaseRow[],
  options: {
    articleId: string | null;
    selectedArticle?: Article;
    propertyTaxRentalChange?: PropertyTaxRentalChangeModel;
    startDate: LocalDate;
    today: LocalDate;
    forceStartDate?: boolean;
    context: { currencyCode?: string; rentalObjectSize?: number; invoicePeriod?: number };
    languageService: LanguageService;
    baselineRow?: LeaseRow;
    baselineOriginalToDate?: LocalDate | null;
    baselineRowClosedByUi?: boolean;
    baselineSignature?: PropertyTaxRowSignature | null;
    currentSignature?: PropertyTaxRowSignature | null;
    activeRow?: LeaseRow;
    uiReplacementRow?: LeaseRow;
    mutationMode?: PropertyTaxRowMutationMode;
  }
): PropertyTaxRowMutationResult {
  const rows = leaseRows ?? [];
  const baselineRow = options.baselineRow;
  const activeRow = options.activeRow;
  const hasBaselineScenario = !!baselineRow && isPersistedLeaseRow(baselineRow);
  const uiReplacementRow =
    options.uiReplacementRow && rows.includes(options.uiReplacementRow)
      ? options.uiReplacementRow
      : activeRow && rows.includes(activeRow) && !isPersistedLeaseRow(activeRow)
        ? activeRow
        : undefined;
  const rowToEditInPlace = hasBaselineScenario ? baselineRow : activeRow;
  const matchesBaseline = hasBaselineScenario && propertyTaxRowSignaturesMatch(options.currentSignature, options.baselineSignature);
  const mutationMode = options.mutationMode ?? 'replaceFromToday';

  if (mutationMode === 'closeOnly') {
    const closeDate = LocalDate.fromDate(options.startDate.clone().addDays(-1));
    if (uiReplacementRow) {
      removeLeaseRow(rows, uiReplacementRow);
    }
    const closedBaseline = closePropertyTaxRow(rows, baselineRow, closeDate);
    if (activeRow && activeRow !== baselineRow && activeRow !== uiReplacementRow) {
      closePropertyTaxRow(rows, activeRow, closeDate);
    }

    return {
      baselineRowClosedByUi: closedBaseline || !!options.baselineRowClosedByUi,
    };
  }

  if (!options.articleId || !options.selectedArticle) {
    if (uiReplacementRow) {
      removeLeaseRow(rows, uiReplacementRow);
    }
    if (baselineRow && options.baselineRowClosedByUi) {
      restoreBaselinePropertyTaxRow(baselineRow, options.baselineOriginalToDate);
    }
    return {
      targetRow: baselineRow,
      baselineRowClosedByUi: false,
    };
  }

  if (rowToEditInPlace && (mutationMode === 'editInPlace' || isFutureFromDate(rowToEditInPlace, options.today))) {
    if (hasBaselineScenario && uiReplacementRow && uiReplacementRow !== rowToEditInPlace) {
      removeLeaseRow(rows, uiReplacementRow);
    }
    if (hasBaselineScenario && options.baselineRowClosedByUi && baselineRow) {
      restoreBaselinePropertyTaxRow(baselineRow, options.baselineOriginalToDate);
    }

    updatePropertyTaxRowInline(rowToEditInPlace, options.selectedArticle, options.propertyTaxRentalChange, options.languageService, {
      ...options.context,
      startDate: options.startDate,
      forceStartDate: options.forceStartDate,
      forceHydrateCalculatedAmounts: isPersistedLeaseRow(rowToEditInPlace),
      overwritePropertyTaxAmounts: mutationMode === 'editInPlace',
    });
    if (mutationMode === 'editInPlace') {
      (rowToEditInPlace as any)._toDate = undefined;
    }

    return {
      targetRow: rowToEditInPlace,
      uiReplacementRow: !isPersistedLeaseRow(rowToEditInPlace) ? rowToEditInPlace : undefined,
      baselineRowClosedByUi: false,
    };
  }

  if (mutationMode === 'replaceFromToday' && hasBaselineScenario && matchesBaseline) {
    if (uiReplacementRow) {
      removeLeaseRow(rows, uiReplacementRow);
    }
    if (baselineRow && options.baselineRowClosedByUi) {
      restoreBaselinePropertyTaxRow(baselineRow, options.baselineOriginalToDate);
    }
    return {
      targetRow: baselineRow,
      baselineRowClosedByUi: false,
    };
  }

  if (mutationMode === 'replaceFromToday' && hasBaselineScenario && baselineRow) {
    baselineRow.toDate = LocalDate.fromDate(options.today.clone().addDays(-1));

    const { row, createdByUi } = getOrCreateUiPropertyTaxRow(
      uiReplacementRow,
      options.startDate,
      options.selectedArticle,
      options.propertyTaxRentalChange,
      options.context,
      options.languageService,
      { forceStartDate: options.forceStartDate }
    );

    if (createdByUi) {
      rows.push(row);
    }

    return {
      targetRow: row,
      uiReplacementRow: row,
      baselineRowClosedByUi: true,
    };
  }

  if (uiReplacementRow) {
    updatePropertyTaxRowInline(uiReplacementRow, options.selectedArticle, options.propertyTaxRentalChange, options.languageService, {
      ...options.context,
      startDate: options.startDate,
      forceStartDate: options.forceStartDate,
      forceHydrateCalculatedAmounts: false,
    });

    return {
      targetRow: uiReplacementRow,
      uiReplacementRow,
      baselineRowClosedByUi: false,
    };
  }

  const result = upsertPropertyTaxLeaseRow(rows, options.selectedArticle, options.startDate, options.context, options.languageService, {
    existingRow: activeRow,
    propertyTaxRentalChange: options.propertyTaxRentalChange,
    forceStartDate: options.forceStartDate,
  });

  return {
    targetRow: result.targetRow,
    uiReplacementRow: !isPersistedLeaseRow(result.targetRow) ? result.targetRow : undefined,
    baselineRowClosedByUi: false,
  };
}

export function isPersistedLeaseRow(row?: LeaseRow): boolean {
  return !!row?.id && row.id.trim().length > 0;
}

export function isFutureFromDate(row: LeaseRow, today: LocalDate): boolean {
  return !!row?.fromDate && row.fromDate.hasValue() && LocalDate.GreaterThan(row.fromDate, today);
}

export function updatePropertyTaxRowInline(
  row: LeaseRow,
  selectedArticle: Article,
  propertyTaxChange: PropertyTaxRentalChangeModel | undefined,
  languageService: LanguageService,
  context: {
    currencyCode?: string;
    rentalObjectSize?: number;
    invoicePeriod?: number;
    startDate?: LocalDate;
    forceStartDate?: boolean;
    forceHydrateCalculatedAmounts?: boolean;
    overwritePropertyTaxAmounts?: boolean;
  }
): void {
  row.article = selectedArticle;
  ensureInvoiceText(row, row.article, languageService, languageService.currentCultureLang);
  row.currencyCode = context.currencyCode ?? row.currencyCode ?? selectedArticle?.currencyCode ?? Currency.SEK;
  row.rentalObjectSize = context.rentalObjectSize;
  row.leaseAgreementInvoicePeriod = context.invoicePeriod;
  ensurePropertyTaxAmounts(
    row,
    propertyTaxChange,
    context.currencyCode,
    context.forceHydrateCalculatedAmounts === true,
    context.overwritePropertyTaxAmounts === true
  );
  if (context.startDate && (context.forceStartDate || !row.fromDate || !row.fromDate.hasValue())) {
    row.fromDate = LocalDate.fromDate(context.startDate);
  }
}

export function getPropertyTaxAssessmentUnitTypeString(
  assessmentUnit: Map<number, TaxTypeCode> | { [key: string]: TaxTypeCode } | undefined,
  languageService: LanguageService
): string {
  if (!assessmentUnit) return languageService.instant('PROPERTY_TAX.NO_ASSESSMENT_UNIT');

  const entry =
    assessmentUnit instanceof Map
      ? Array.from(assessmentUnit.entries())[0]
      : Object.keys(assessmentUnit as any).length
        ? [Number(Object.keys(assessmentUnit as any)[0]), (assessmentUnit as any)[Object.keys(assessmentUnit as any)[0]]]
        : null;

  if (!entry || !Number.isFinite(entry[0]) || !entry[1]) {
    return languageService.instant('PROPERTY_TAX.NO_ASSESSMENT_UNIT');
  }

  const [code, type] = entry as [number, TaxTypeCode];
  return `${code}, ${type.name}`;
}

export function upsertPropertyTaxLeaseRow(
  leaseRows: LeaseRow[],
  selectedArticle: Article,
  startDate: LocalDate,
  context: { currencyCode?: string; rentalObjectSize?: number; invoicePeriod?: number },
  languageService: LanguageService,
  options?: {
    existingRow?: LeaseRow;
    cultureName?: string;
    propertyTaxRentalChange?: PropertyTaxRentalChangeModel;
    forceHydrateCalculatedAmounts?: boolean;
    forceStartDate?: boolean;
  }
): { targetRow: LeaseRow; closedRow?: LeaseRow; created: boolean } {
  const rows = leaseRows ?? [];
  const existingRow = options?.existingRow;

  if (existingRow) {
    existingRow.article = selectedArticle;
    ensureInvoiceText(existingRow, existingRow.article, languageService, options?.cultureName);
    existingRow.currencyCode = context.currencyCode ?? existingRow.currencyCode;
    existingRow.rentalObjectSize = context.rentalObjectSize;
    existingRow.leaseAgreementInvoicePeriod = context.invoicePeriod;
    if (options?.forceStartDate || !existingRow.fromDate || !existingRow.fromDate.hasValue()) {
      existingRow.fromDate = LocalDate.fromDate(startDate);
    }
    ensurePropertyTaxAmounts(
      existingRow,
      options?.propertyTaxRentalChange,
      context.currencyCode,
      options?.forceHydrateCalculatedAmounts === true
    );
    return { targetRow: existingRow, created: false };
  }

  const closedRow = getLatestPropertyTaxRow(rows);
  if (closedRow) {
    closedRow.toDate = LocalDate.fromDate(startDate.clone().addDays(-1));
  }

  const newRow = new LeaseRow(context.currencyCode ?? selectedArticle?.currencyCode ?? Currency.SEK);
  newRow.article = selectedArticle;
  ensureInvoiceText(newRow, newRow.article, languageService, options?.cultureName);
  newRow.fromDate = LocalDate.fromDate(startDate);
  newRow.rentalObjectSize = context.rentalObjectSize;
  newRow.leaseAgreementInvoicePeriod = context.invoicePeriod;
  ensurePropertyTaxAmounts(newRow, options?.propertyTaxRentalChange, context.currencyCode, options?.forceHydrateCalculatedAmounts === true);

  rows.push(newRow);

  return { targetRow: newRow, closedRow, created: true };
}
